import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Button, Card, Input, InputNumber } from 'antd';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { Check, Edit2, Minus, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

const MASTER_CALC_STORAGE_KEY = 'nexrise.masterCalc.items';
const SALES_MASTER_CALC_STORAGE_KEY = 'nexrise.salesMasterCalc.items';

const createEmptyItem = (id = Date.now()) => ({
  id,
  productId: '',
  sku: '',
  name: '',
  description: '',
  landingRate: '',
  catalogRate: '',
  qty: '',
  weight: 0,
  total: 0
});

const createEmptyGroup = (id = Date.now()) => ({
  id,
  title: '',
  sets: 1,
  searchValue: '',
  items: []
});

const getGroupSets = (group) => {
  const sets = Number(group?.sets);
  return Number.isFinite(sets) ? Math.max(1, sets) : 1;
};

const getTotalQty = (group, item) => (Number(item.qty) || 0) * getGroupSets(group);

const getItemTotal = (group, item) => (Number(item.landingRate) || 0) * getTotalQty(group, item);

const createItemFromProduct = (product, qty, id = Date.now(), priceField = 'landingRate') => {
  const landingRate = Number(product[priceField]) || 0;
  const normalizedQty = Number(qty) || 0;

  return {
    id,
    productId: product.id || '',
    sku: product.sku || '',
    name: product.name || '',
    description: product.description || '',
    landingRate,
    catalogRate: landingRate,
    qty: normalizedQty,
    weight: Number(product.weight) || 0,
    total: landingRate * normalizedQty
  };
};

const normalizeSavedItem = (item, id, groupSets = 1) => {
  const normalizedItem = {
    ...createEmptyItem(id),
    ...item,
    id: item.id || id
  };

  return {
    ...normalizedItem,
    total: (Number(normalizedItem.landingRate) || 0) * (Number(normalizedItem.qty) || 0) * groupSets
  };
};

const loadSavedGroups = (storageKey = MASTER_CALC_STORAGE_KEY) => {
  try {
    const savedItems = localStorage.getItem(storageKey);
    if (!savedItems) return [createEmptyGroup(1)];

    const parsedItems = JSON.parse(savedItems);
    if (!Array.isArray(parsedItems) || parsedItems.length === 0) {
      return [createEmptyGroup(1)];
    }

    if (parsedItems.some((entry) => Array.isArray(entry.items))) {
      return parsedItems.map((group, groupIndex) => {
        const groupId = group.id || Date.now() + groupIndex;
        const groupSets = group.sets === undefined ? 1 : getGroupSets(group);

        return {
          ...createEmptyGroup(groupId),
          ...group,
          id: groupId,
          sets: groupSets,
          searchValue: '',
          items: (group.items || []).map((item, itemIndex) => (
            normalizeSavedItem(item, Date.now() + groupIndex + itemIndex + 1, groupSets)
          ))
        };
      });
    }

    return [{
      ...createEmptyGroup(1),
      items: parsedItems.map((item, index) => normalizeSavedItem(item, Date.now() + index, 1))
    }];
  } catch (error) {
    console.error('Error loading MasterCalc draft:', error);
    return [createEmptyGroup(1)];
  }
};

const AdminMasterCalc = ({ db, mode = 'admin' }) => {
  const isSalesMode = mode === 'sales';
  const storageKey = isSalesMode ? SALES_MASTER_CALC_STORAGE_KEY : MASTER_CALC_STORAGE_KEY;
  const priceField = isSalesMode ? 'rate' : 'landingRate';
  const rateLabel = isSalesMode ? 'Sell Rate' : 'Landing rate';
  const [groups, setGroups] = useState(() => loadSavedGroups(storageKey));
  const [allProducts, setAllProducts] = useState([]);
  const [bomProducts, setBomProducts] = useState([]);
  const [editingRateId, setEditingRateId] = useState(null);
  const [editingQtyId, setEditingQtyId] = useState(null);
  const [editingSetsGroupId, setEditingSetsGroupId] = useState(null);
  const [rateDraft, setRateDraft] = useState('');
  const [duplicateItemId, setDuplicateItemId] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [editingGroupTitleId, setEditingGroupTitleId] = useState(null);
  const [hoveredGroupTitleId, setHoveredGroupTitleId] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const groupSearchRefs = useRef({});
  const qtyRefs = useRef({});
  const rateInputRef = useRef(null);
  const pendingFocusGroupId = useRef(null);
  const recentCatalogActionRef = useRef({});

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [prodSnap, bomSnap] = await Promise.all([
          getDocs(collection(db, 'products')),
          getDocs(collection(db, 'bom_products'))
        ]);
        const products = prodSnap.docs.map((productDoc) => ({ id: productDoc.id, ...productDoc.data() }));
        setAllProducts(products);
        setBomProducts(bomSnap.docs.map((bomDoc) => ({ id: bomDoc.id, ...bomDoc.data() })));

        if (isSalesMode) {
          const catalogRatesByProductId = new Map(products.map((product) => [
            product.id,
            Number(product.rate) || 0
          ]));

          setGroups((currentGroups) => currentGroups.map((group) => ({
            ...group,
            items: group.items.map((item) => (
              item.productId && catalogRatesByProductId.has(item.productId)
                ? { ...item, catalogRate: catalogRatesByProductId.get(item.productId) }
                : item
            ))
          })));
        }
      } catch (error) {
        console.error('Error loading catalogs for MasterCalc:', error);
      }
    };

    loadCatalogs();
  }, [db, isSalesMode]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(groups));
    } catch (error) {
      console.error('Error saving MasterCalc draft:', error);
    }
  }, [groups, storageKey]);

  const productsBySku = useMemo(() => {
    const lookup = new Map();
    allProducts.forEach((product) => {
      if (product.sku) lookup.set(String(product.sku), product);
    });
    return lookup;
  }, [allProducts]);

  const bomProductsBySku = useMemo(() => {
    const lookup = new Map();
    bomProducts.forEach((product) => {
      if (product.sku) lookup.set(String(product.sku), product);
    });
    return lookup;
  }, [bomProducts]);

  const productSearchOptions = useMemo(() => {
    const presetSkus = new Set(
      bomProducts
        .filter((product) => product.type === 'preset' && product.sku)
        .map((product) => String(product.sku))
    );

    const presetOptions = bomProducts
      .filter((product) => product.type === 'preset')
      .map((product) => ({
        value: product.sku || '',
        product,
        itemType: 'preset',
        searchText: `${product.sku || ''} ${product.name || ''}`.toLowerCase(),
        label: (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: '700', fontSize: '12px' }}>{product.sku || 'No SKU'} - Preset</div>
            <div style={{ fontSize: '11px', color: '#64748b' }}>{product.name || 'Unnamed preset'}</div>
          </div>
        )
      }));

    const productOptions = allProducts
      .filter((product) => !presetSkus.has(String(product.sku || '')))
      .map((product) => ({
        value: product.sku || '',
        product,
        itemType: 'product',
        searchText: `${product.sku || ''} ${product.name || ''}`.toLowerCase(),
        label: (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: '700', fontSize: '12px' }}>{product.sku || 'No SKU'}</div>
            <div style={{ fontSize: '11px', color: '#64748b' }}>{product.name || 'Unnamed product'}</div>
          </div>
        )
      }));

    return [...presetOptions, ...productOptions];
  }, [allProducts, bomProducts]);

  const allItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const subTotal = groups.reduce((groupSum, group) => (
    groupSum + group.items.reduce((itemSum, item) => itemSum + getItemTotal(group, item), 0)
  ), 0);
  const gst = subTotal * 0.18;
  const grandTotal = subTotal + gst;
  const totalWeight = groups.reduce((groupSum, group) => (
    groupSum + group.items.reduce((itemSum, item) => {
      const weight = Number(item.weight) || 0;
      return itemSum + (weight * getTotalQty(group, item));
    }, 0)
  ), 0);

  useEffect(() => {
    if (!pendingFocusGroupId.current) return;

    const focusId = pendingFocusGroupId.current;
    pendingFocusGroupId.current = null;
    setTimeout(() => groupSearchRefs.current[focusId]?.focus(), 0);
  }, [groups]);

  useEffect(() => {
    if (!duplicateItemId) return undefined;

    const timer = setTimeout(() => {
      setDuplicateItemId(null);
    }, 1800);

    return () => clearTimeout(timer);
  }, [duplicateItemId]);

  const updateGroupTitle = (groupId, value) => {
    setGroups((currentGroups) => currentGroups.map((group) => (
      group.id === groupId ? { ...group, title: value } : group
    )));
  };

  const updateGroupSearch = (groupId, value) => {
    setGroups((currentGroups) => currentGroups.map((group) => (
      group.id === groupId ? { ...group, searchValue: value } : group
    )));
  };

  const updateGroupSets = (groupId, value) => {
    const nextSets = Math.max(1, Number(value) || 1);

    setGroups((currentGroups) => currentGroups.map((group) => {
      if (group.id !== groupId) return group;

      return {
        ...group,
        sets: nextSets,
        items: group.items.map((item) => ({
          ...item,
          total: (Number(item.landingRate) || 0) * (Number(item.qty) || 0) * nextSets
        }))
      };
    }));
  };

  const adjustGroupSets = (groupId, delta) => {
    const group = groups.find((currentGroup) => currentGroup.id === groupId);
    updateGroupSets(groupId, getGroupSets(group) + delta);
  };

  const updateItem = (groupId, itemId, field, value) => {
    setGroups((currentGroups) => currentGroups.map((group) => {
      if (group.id !== groupId) return group;
      const groupSets = getGroupSets(group);

      return {
        ...group,
        items: group.items.map((item) => {
          if (item.id !== itemId) return item;

          const updatedItem = { ...item, [field]: value };
          const landingRate = Number(updatedItem.landingRate) || 0;
          const qty = Number(updatedItem.qty) || 0;

          return {
            ...updatedItem,
            total: landingRate * qty * groupSets
          };
        })
      };
    }));
  };

  const adjustItemQty = (groupId, item, delta) => {
    const nextQty = Math.max(0, (Number(item.qty) || 0) + delta);
    updateItem(groupId, item.id, 'qty', nextQty);
  };

  const addItemsToGroup = (groupId, componentItems, presetName = '') => {
    let highlightedItemId = null;

    setGroups((currentGroups) => currentGroups.map((group) => {
      if (group.id !== groupId) return group;

      const nextItems = [...group.items];
      const groupSets = getGroupSets(group);

      componentItems.forEach((componentItem) => {
        const existingIndex = nextItems.findIndex((item) =>
          (componentItem.productId && item.productId === componentItem.productId) ||
          (componentItem.sku && item.sku === componentItem.sku)
        );

        if (existingIndex > -1) {
          const existingItem = nextItems[existingIndex];
          const nextQty = (Number(existingItem.qty) || 0) + (Number(componentItem.qty) || 0);
          const landingRate = Number(existingItem.landingRate) || 0;

          nextItems[existingIndex] = {
            ...existingItem,
            qty: nextQty,
            total: landingRate * nextQty * groupSets
          };
          highlightedItemId = existingItem.id;
          return;
        }

        nextItems.push({
          ...componentItem,
          total: (Number(componentItem.landingRate) || 0) * (Number(componentItem.qty) || 0) * groupSets
        });
        highlightedItemId = componentItem.id;
      });

      return {
        ...group,
        title: presetName || group.title,
        searchValue: '',
        items: nextItems
      };
    }));

    setDuplicateItemId(highlightedItemId);
  };

  const expandPresetIntoGroup = (groupId, preset) => {
    const components = Array.isArray(preset.presetComponents) ? preset.presetComponents : [];

    if (components.length === 0) {
      toast.error(`${preset.sku || 'This preset'} has no components.`);
      return;
    }

    const missingSkus = [];
    const componentItems = components
      .map((component, index) => {
        const product = productsBySku.get(String(component.sku || ''));

        if (!product) {
          missingSkus.push(component.sku);
          return null;
        }

        return createItemFromProduct(product, Number(component.qty) || 0, Date.now() + index, priceField);
      })
      .filter(Boolean);

    if (componentItems.length === 0) {
      toast.error(`No matching products found for ${preset.sku || 'this preset'}.`);
      return;
    }

    const presetTitle = preset.name && preset.sku
      ? `${preset.name} (${preset.sku})`
      : preset.name || preset.sku || '';

    addItemsToGroup(groupId, componentItems, presetTitle);
    toast.success(`Added ${componentItems.length} components from ${preset.sku || 'preset'}.`);
    if (missingSkus.length) {
      toast.error(`Missing Components: ${missingSkus.join(', ')}.`);
    }
  };

  const selectProduct = (groupId, product) => {
    const matchingBomProduct = bomProductsBySku.get(String(product.sku || ''));

    if (matchingBomProduct?.type === 'preset') {
      expandPresetIntoGroup(groupId, matchingBomProduct);
      return;
    }

    addItemsToGroup(groupId, [createItemFromProduct(product, 1, Date.now(), priceField)]);
    toast.success(`${product.sku || 'This product'} added.`);
  };

  const selectCatalogItem = (groupId, option) => {
    const selectedSku = String(option?.value || option?.product?.sku || '').trim();
    if (selectedSku && shouldSkipRecentCatalogAction(groupId, selectedSku)) return;

    if (option.itemType === 'preset') {
      expandPresetIntoGroup(groupId, option.product);
      return;
    }

    selectProduct(groupId, option.product);
  };

  const resolveExactSku = (groupId, sku) => {
    const normalizedSku = String(sku || '').trim();
    if (!normalizedSku) return;
    if (shouldSkipRecentCatalogAction(groupId, normalizedSku)) return;

    const matchingBomProduct = bomProductsBySku.get(normalizedSku);
    if (matchingBomProduct?.type === 'preset') {
      expandPresetIntoGroup(groupId, matchingBomProduct);
      return;
    }

    const matchingProduct = productsBySku.get(normalizedSku);
    if (matchingProduct) {
      selectProduct(groupId, matchingProduct);
    }
  };

  const shouldSkipRecentCatalogAction = (groupId, sku) => {
    const now = Date.now();
    const recentAction = recentCatalogActionRef.current[groupId];

    if (recentAction?.sku === sku && now - recentAction.time < 500) {
      return true;
    }

    recentCatalogActionRef.current[groupId] = { sku, time: now };
    return false;
  };

  const addGroup = () => {
    const nextGroupId = Date.now();
    pendingFocusGroupId.current = nextGroupId;
    setGroups((currentGroups) => [
      ...currentGroups,
      createEmptyGroup(nextGroupId)
    ]);
  };

  const removeItem = (groupId, itemId) => {
    setGroups((currentGroups) => currentGroups.map((group) => (
      group.id === groupId
        ? { ...group, items: group.items.filter((item) => item.id !== itemId) }
        : group
    )));
  };

  const removeGroup = (groupId) => {
    setGroups((currentGroups) => {
      const nextGroups = currentGroups.filter((group) => group.id !== groupId);
      return nextGroups.length ? nextGroups : [createEmptyGroup(Date.now())];
    });
  };

  const resetCalculator = () => {
    const emptyGroupId = Date.now();
    pendingFocusGroupId.current = emptyGroupId;
    setGroups([createEmptyGroup(emptyGroupId)]);
    setEditingRateId(null);
    setEditingQtyId(null);
    setEditingSetsGroupId(null);
    setEditingGroupTitleId(null);
    setHoveredGroupTitleId(null);
    setRateDraft('');
    setDuplicateItemId(null);
    groupSearchRefs.current = {};
    qtyRefs.current = {};
    recentCatalogActionRef.current = {};
    localStorage.removeItem(storageKey);
  };

  const startEditingRate = (item) => {
    setEditingRateId(item.id);
    setEditingQtyId(null);
    setEditingSetsGroupId(null);
    setRateDraft(item.landingRate);
    setTimeout(() => rateInputRef.current?.focus(), 0);
  };

  const confirmRateUpdate = async (id) => {
    const item = allItems.find((currentItem) => currentItem.id === id);
    if (!item?.productId) {
      toast.error(`Select a product before updating ${rateLabel.toLowerCase()}.`);
      return;
    }

    const nextLandingRate = Number(rateDraft) || 0;

    if (isSalesMode) {
      const group = groups.find((currentGroup) => currentGroup.items.some((currentItem) => currentItem.id === id));
      if (group) updateItem(group.id, id, 'landingRate', nextLandingRate);
      setEditingRateId(null);
      setRateDraft('');
      toast.success(`Sell rate updated for ${item.sku || item.name || 'product'}.`);
      return;
    }

    try {
      await updateDoc(doc(db, 'products', item.productId), {
        landingRate: nextLandingRate
      });

      const group = groups.find((currentGroup) => currentGroup.items.some((currentItem) => currentItem.id === id));
      if (group) updateItem(group.id, id, 'landingRate', nextLandingRate);
      setAllProducts((currentProducts) => currentProducts.map((product) => (
        product.id === item.productId
          ? { ...product, landingRate: nextLandingRate }
          : product
      )));
      setEditingRateId(null);
      setRateDraft('');
      toast.success(`Landing rate updated for ${item.sku || item.name || 'product'}.`);
    } catch (error) {
      console.error('Error updating landing rate:', error);
      toast.error('Unable to update landing rate: ' + error.message);
    }
  };

  const cancelRateUpdate = () => {
    setEditingRateId(null);
    setRateDraft('');
  };

  const isSellRateOverridden = (item) => (
    isSalesMode && Number(item.landingRate || 0) !== Number(item.catalogRate || 0)
  );

  const resetSellRate = (groupId, item) => {
    updateItem(groupId, item.id, 'landingRate', Number(item.catalogRate) || 0);
    toast.success(`Sell rate reset for ${item.sku || item.name || 'product'}.`);
  };

  const startEditingQty = (item) => {
    setEditingQtyId(item.id);
    setEditingRateId(null);
    setEditingSetsGroupId(null);
  };

  const startEditingGroupTitle = (groupId) => {
    setEditingGroupTitleId(groupId);
    setEditingRateId(null);
    setEditingQtyId(null);
    setEditingSetsGroupId(null);
  };

  const isMobile = windowWidth <= 768;
  const containerStyle = {
    padding: isMobile ? '12px' : '20px'
  };
  const responsivePageGridStyle = {
    ...pageGridStyle,
    ...(isMobile ? mobilePageGridStyle : {})
  };
  const responsivePanelStyle = {
    ...panelStyle,
    ...(isMobile ? mobilePanelStyle : {})
  };
  const responsiveSummaryPanelStyle = {
    ...summaryPanelStyle,
    ...(isMobile ? mobileSummaryPanelStyle : {})
  };
  const responsiveItemGridStyle = {
    ...itemGridStyle,
    ...(isMobile ? mobileItemGridStyle : {})
  };
  const responsiveRowGridStyle = {
    ...rowGridStyle,
    ...(isMobile ? mobileRowGridStyle : {})
  };
  const responsiveHeaderGridStyle = {
    ...headerGridStyle,
    ...(isMobile ? mobileHeaderGridStyle : {})
  };
  const responsiveGroupToolbarStyle = {
    ...groupToolbarStyle,
    ...(isMobile ? mobileGroupToolbarStyle : {})
  };
  const responsiveHeadingStyle = {
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: 'bold',
    margin: isMobile ? '0 0 14px 0' : '0 0 20px 0'
  };

  return (
    <div style={containerStyle}>
      <h1 style={responsiveHeadingStyle}>MasterCalc</h1>

      <div style={responsivePageGridStyle}>
        <div style={groupsColumnStyle}>
          {groups.map((group) => {
            const groupSubTotal = group.items.reduce((sum, item) => sum + getItemTotal(group, item), 0);

            return (
              <div key={group.id} style={responsivePanelStyle}>
                <div style={responsiveGroupToolbarStyle}>
                  {editingGroupTitleId === group.id ? (
                    <Input
                      value={group.title}
                      onChange={(event) => updateGroupTitle(group.id, event.target.value)}
                      onBlur={() => setEditingGroupTitleId(null)}
                      onPressEnter={() => setEditingGroupTitleId(null)}
                      placeholder="Group title"
                      size="large"
                      autoFocus
                      style={groupTitleInputStyle}
                    />
                  ) : (
                    <div
                      style={groupTitleDisplayStyle}
                      onClick={() => startEditingGroupTitle(group.id)}
                      onMouseEnter={() => setHoveredGroupTitleId(group.id)}
                      onMouseLeave={() => setHoveredGroupTitleId(null)}
                      title="Edit group title"
                    >
                      <span style={groupTitleTextStyle}>{group.title || 'Untitled group'}</span>
                      <span style={groupCountPillStyle}>
                        {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                      </span>
                      <Edit2
                        size={14}
                        style={{
                          ...groupTitleEditIconStyle,
                          opacity: hoveredGroupTitleId === group.id ? 1 : 0
                        }}
                      />
                    </div>
                  )}
                  <div style={{ ...setsControlStyle, gridColumn: isMobile ? 'auto' : 3 }}>
                    <span style={setsLabelStyle}>Sets</span>
                    <div style={setsStepperStyle}>
                      <Button
                        onClick={() => adjustGroupSets(group.id, -1)}
                        disabled={getGroupSets(group) <= 1}
                        style={{
                          ...setsStepperButtonStyle,
                          ...(getGroupSets(group) <= 1 ? disabledSetsStepperButtonStyle : {})
                        }}
                        title="Decrease sets"
                        tabIndex={-1}
                        icon={<Minus size={14} />}
                      />
                      {editingSetsGroupId === group.id ? (
                        <InputNumber
                          value={getGroupSets(group)}
                          min={1}
                          step={1}
                          precision={0}
                          controls={false}
                          autoFocus
                          onChange={(value) => updateGroupSets(group.id, value ?? 1)}
                          onBlur={() => setEditingSetsGroupId(null)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              setEditingSetsGroupId(null);
                            }
                          }}
                          parser={(value) => String(value || '').replace(/[^\d]/g, '')}
                          style={setsInputStyle}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSetsGroupId(group.id);
                            setEditingRateId(null);
                            setEditingQtyId(null);
                            setEditingGroupTitleId(null);
                          }}
                          style={setsValueButtonStyle}
                          title="Edit sets"
                        >
                          {getGroupSets(group).toLocaleString('en-IN')}
                        </button>
                      )}
                      <Button
                        onClick={() => adjustGroupSets(group.id, 1)}
                        style={setsStepperButtonStyle}
                        title="Increase sets"
                        tabIndex={-1}
                        icon={<Plus size={14} />}
                      />
                    </div>
                  </div>
                  <AutoComplete
                    ref={(element) => {
                      if (element) {
                        groupSearchRefs.current[group.id] = element;
                      } else {
                        delete groupSearchRefs.current[group.id];
                      }
                    }}
                    options={productSearchOptions}
                    value={group.searchValue}
                    onChange={(value) => updateGroupSearch(group.id, value)}
                    onSelect={(_, option) => selectCatalogItem(group.id, option)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        resolveExactSku(group.id, group.searchValue);
                      }
                    }}
                    placeholder="Search component or preset..."
                    filterOption={(inputValue, option) => option?.searchText?.includes(String(inputValue || '').toLowerCase())}
                    notFoundContent="No matching product"
                    placement="bottomLeft"
                    popupMatchSelectWidth
                    size="large"
                    style={{ width: '100%', gridColumn: isMobile ? 'auto' : 4 }}
                  />
                </div>

                <div style={responsiveHeaderGridStyle}>
                  <div style={labelStyle}>SKU</div>
                  <div style={labelStyle}>Name</div>
                  <div style={labelStyle}>{rateLabel}</div>
                  <div style={labelStyle}>Base Qty</div>
                  <div style={labelStyle}>Total Qty</div>
                  <div style={labelStyle}>Total</div>
                  <div style={labelStyle}></div>
                </div>

                <div style={responsiveItemGridStyle}>
                  {group.items.length === 0 ? (
                    <div style={emptyGroupStyle}>Search a component or preset to add parts to this group.</div>
                  ) : (
                    group.items.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          ...responsiveRowGridStyle,
                          ...(hoveredItemId === item.id ? rowHoverStyle : {}),
                          ...(duplicateItemId === item.id ? duplicateRowStyle : {})
                        }}
                        onMouseEnter={() => setHoveredItemId(item.id)}
                        onMouseLeave={() => setHoveredItemId(null)}
                      >
                        <div style={skuTextCellStyle}>{item.sku || '-'}</div>
                        <div style={nameTextCellStyle}>{item.name || '-'}</div>
                        {editingRateId === item.id ? (
                          <div style={rateEditCellStyle}>
                            <InputNumber
                              ref={rateInputRef}
                              value={rateDraft}
                              disabled={!item.productId}
                              onChange={(value) => setRateDraft(value ?? '')}
                              controls={false}
                              size="large"
                              style={{
                                width: '100%',
                                ...rateInputStyle,
                                ...(!item.productId ? disabledInputStyle : {}),
                                ...(duplicateItemId === item.id ? duplicateFieldStyle : {})
                              }}
                            />
                            <Button
                              onClick={() => confirmRateUpdate(item.id)}
                              style={confirmButtonStyle}
                              title="Update price"
                              tabIndex={-1}
                              icon={<Check size={15} />}
                            />
                            <Button
                              onClick={cancelRateUpdate}
                              style={cancelButtonStyle}
                              title="Cancel"
                              tabIndex={-1}
                              icon={<X size={15} />}
                            />
                          </div>
                        ) : (
                          <div
                            style={editableTextCellStyle}
                            onClick={() => startEditingRate(item)}
                            title={`Edit ${rateLabel.toLowerCase()}`}
                          >
                            <span>Rs. {Number(item.landingRate || 0).toLocaleString('en-IN')}</span>
                            {isSellRateOverridden(item) && (
                              <Button
                                type="text"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  resetSellRate(group.id, item);
                                }}
                                style={resetSellRateButtonStyle}
                                title="Reset to catalog rate"
                                tabIndex={-1}
                                icon={<RotateCcw size={13} />}
                              />
                            )}
                            <Edit2
                              size={13}
                              style={{
                                ...inlineEditIconStyle,
                                opacity: hoveredItemId === item.id ? 1 : 0
                              }}
                            />
                          </div>
                        )}
                        {editingQtyId === item.id ? (
                          <InputNumber
                            ref={(element) => {
                              if (element) {
                                qtyRefs.current[item.id] = element;
                                setTimeout(() => element.focus?.(), 0);
                              } else {
                                delete qtyRefs.current[item.id];
                              }
                            }}
                            value={item.qty}
                            disabled={!item.productId}
                            onChange={(value) => updateItem(group.id, item.id, 'qty', value ?? '')}
                            onBlur={() => setEditingQtyId(null)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                setEditingQtyId(null);
                              }
                            }}
                            min={0}
                            step={1}
                            precision={0}
                            parser={(value) => String(value || '').replace(/[^\d]/g, '')}
                            size="large"
                            controls={false}
                            style={{
                              width: '100%',
                              ...inputStyle,
                              ...(!item.productId ? disabledInputStyle : {})
                            }}
                          />
                        ) : (
                          <div style={qtyStepperStyle}>
                            <Button
                              onClick={(event) => {
                                event.stopPropagation();
                                adjustItemQty(group.id, item, -1);
                              }}
                              disabled={!item.productId}
                              style={{
                                ...qtyStepperButtonStyle,
                                ...(!item.productId ? disabledQtyStepperButtonStyle : {})
                              }}
                              title="Decrease quantity"
                              tabIndex={-1}
                              icon={<Minus size={14} />}
                            />
                            <button
                              type="button"
                              onClick={() => startEditingQty(item)}
                              style={qtyValueButtonStyle}
                              title="Edit quantity"
                            >
                              {Number(item.qty || 0).toLocaleString('en-IN')}
                            </button>
                            <Button
                              onClick={(event) => {
                                event.stopPropagation();
                                adjustItemQty(group.id, item, 1);
                              }}
                              disabled={!item.productId}
                              style={{
                                ...qtyStepperButtonStyle,
                                ...(!item.productId ? disabledQtyStepperButtonStyle : {})
                              }}
                              title="Increase quantity"
                              tabIndex={-1}
                              icon={<Plus size={14} />}
                            />
                          </div>
                        )}
                        <div style={totalQtyTextCellStyle}>
                          {getTotalQty(group, item).toLocaleString('en-IN')}
                        </div>
                        <div style={totalTextCellStyle}>Rs. {getItemTotal(group, item).toLocaleString('en-IN')}</div>
                        <Button
                          onClick={() => removeItem(group.id, item.id)}
                          style={{
                            ...deleteButtonStyle,
                            ...(hoveredItemId === item.id ? deleteButtonHoverStyle : {}),
                            ...(duplicateItemId === item.id ? duplicateButtonStyle : {})
                          }}
                          title="Remove item"
                          tabIndex={-1}
                          icon={<Trash2 size={16} />}
                        />
                      </div>
                    ))
                  )}

                  <div style={groupFooterStyle}>
                    <div style={groupTotalBlockStyle}>
                      <div style={groupTotalLabelStyle}>Group Total</div>
                      <div style={groupTotalAmountStyle}>Rs. {groupSubTotal.toLocaleString('en-IN')}</div>
                    </div>
                    <Button
                      danger
                      onClick={() => removeGroup(group.id)}
                      style={removeGroupButtonStyle}
                      disabled={groups.length === 1 && group.items.length === 0 && !group.title}
                    >
                      Remove Group
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          <div style={masterActionsRowStyle}>
            <Button type="primary" onClick={addGroup} style={addButtonStyle}>Add Group</Button>
            <Button danger onClick={resetCalculator} style={resetButtonStyle}>Reset</Button>
          </div>
        </div>

        <Card
          style={{ ...responsiveSummaryPanelStyle, padding: 0 }}
          styles={{ body: { padding: isMobile ? '15px' : '20px' } }}
        >
          <h2 style={summaryTitleStyle}>Summary</h2>
          <div style={summaryRowStyle}>
            <span>Sub Total(excl. tax):</span>
            <strong>Rs. {subTotal.toLocaleString('en-IN')}</strong>
          </div>
          <div style={summaryRowStyle}>
            <span>GST (18%):</span>
            <strong>Rs. {gst.toLocaleString('en-IN')}</strong>
          </div>
          <div style={summaryRowStyle}>
            <span>Total Weight:</span>
            <strong>{totalWeight.toLocaleString('en-IN')} kg</strong>
          </div>
          <div style={summaryDividerStyle} />
          <div style={summaryTotalRowStyle}>
            <span>Grand Total:</span>
            <strong>Rs. {grandTotal.toLocaleString('en-IN')}</strong>
          </div>
        </Card>
      </div>
    </div>
  );
};

const fieldHeight = '40px';

const inputStyle = {
  width: '100%',
  height: fieldHeight,
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  backgroundColor: 'white',
  outline: 'none',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const pageGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 4fr) minmax(220px, 1fr)',
  gap: '20px',
  alignItems: 'start'
};

const mobilePageGridStyle = {
  gridTemplateColumns: '1fr',
  gap: '12px'
};

const groupsColumnStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  minWidth: 0
};

const panelStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  padding: '20px',
  overflowX: 'auto'
};

const mobilePanelStyle = {
  padding: '12px',
  borderRadius: '10px',
  WebkitOverflowScrolling: 'touch'
};

const groupToolbarStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 50%) minmax(0, 15%) minmax(140px, 15%) minmax(180px, 20%)',
  gap: '14px',
  marginBottom: '16px',
  minWidth: '840px'
};

const mobileGroupToolbarStyle = {
  gridTemplateColumns: '1fr',
  minWidth: '900px',
  gap: '10px'
};

const groupTitleInputStyle = {
  ...inputStyle,
  height: '44px',
  fontSize: '18px',
  fontWeight: '700',
  color: '#1e293b',
  backgroundColor: '#f8fafc',
  borderColor: '#cbd5e1'
};

const groupTitleDisplayStyle = {
  minWidth: 0,
  height: '44px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  cursor: 'pointer',
  padding: '0 2px'
};

const groupTitleTextStyle = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '18px',
  fontWeight: '700',
  color: '#0f172a'
};

const groupCountPillStyle = {
  flex: '0 0 auto',
  padding: '3px 10px',
  borderRadius: '999px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  color: '#475569',
  fontSize: '12px',
  fontWeight: '700',
  lineHeight: 1.4
};

const groupTitleEditIconStyle = {
  flex: '0 0 auto',
  color: '#94a3b8',
  transition: 'opacity 140ms ease'
};

const setsControlStyle = {
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  height: '44px'
};

const setsLabelStyle = {
  flex: '0 0 auto',
  fontSize: '12px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.08em'
};

const setsStepperStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0
};

const setsStepperButtonStyle = {
  width: '28px',
  height: '28px',
  minWidth: '28px',
  borderRadius: '999px',
  border: 'none',
  backgroundColor: '#f1f5f9',
  color: '#0f172a',
  boxShadow: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const disabledSetsStepperButtonStyle = {
  backgroundColor: '#f8fafc',
  color: '#cbd5e1',
  cursor: 'not-allowed'
};

const setsInputStyle = {
  width: '38px',
  height: '28px',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  color: '#020617',
  fontWeight: '700',
  fontSize: '14px',
  textAlign: 'center'
};

const setsValueButtonStyle = {
  minWidth: '24px',
  border: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: '#020617',
  fontWeight: '700',
  fontSize: '14px',
  lineHeight: 1,
  textAlign: 'center'
};

const summaryPanelStyle = {
  ...panelStyle,
  position: 'sticky',
  top: '20px',
  alignSelf: 'start',
  height: 'fit-content',
  overflowX: 'visible'
};

const mobileSummaryPanelStyle = {
  position: 'static',
  top: 'auto',
  overflowX: 'visible'
};

const summaryTitleStyle = {
  fontSize: '18px',
  fontWeight: '700',
  margin: '0 0 18px 0',
  color: '#1e293b'
};

const summaryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '8px 0',
  fontSize: '14px',
  color: '#475569'
};

const summaryDividerStyle = {
  borderTop: '1px solid #e2e8f0',
  margin: '12px 0'
};

const summaryTotalRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  fontSize: '16px',
  fontWeight: '700',
  color: '#1e293b'
};

const tableGridColumns = '120px minmax(280px, 1.8fr) 130px 130px 110px 130px 40px';
const mobileTableGridColumns = '120px 240px 110px 110px 100px 110px 40px';

const itemGridStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  minWidth: '840px',
  borderTop: '1px solid #e5e7eb'
};

const mobileItemGridStyle = {
  minWidth: '900px'
};

const headerGridStyle = {
  display: 'grid',
  gridTemplateColumns: tableGridColumns,
  gap: '10px',
  backgroundColor: 'white',
  padding: '0 10px 8px 10px',
  minWidth: '840px',
  alignItems: 'center'
};

const mobileHeaderGridStyle = {
  gridTemplateColumns: mobileTableGridColumns,
  minWidth: '900px',
  gap: '8px'
};

const rowGridStyle = {
  display: 'grid',
  gridTemplateColumns: tableGridColumns,
  gap: '10px',
  alignItems: 'center',
  minHeight: '58px',
  padding: '8px 10px',
  borderBottom: '1px solid #edf2f7',
  transition: 'background-color 140ms ease'
};

const mobileRowGridStyle = {
  gridTemplateColumns: mobileTableGridColumns,
  gap: '8px'
};

const rowHoverStyle = {
  backgroundColor: '#f8fafc'
};

const duplicateRowStyle = {
  backgroundColor: '#fffbeb'
};

const labelStyle = {
  padding: '6px 0',
  fontSize: '12px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.08em'
};

const baseTextCellStyle = {
  minWidth: 0,
  color: '#020617',
  fontSize: '14px',
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const skuTextCellStyle = {
  ...baseTextCellStyle,
  fontSize: '13px',
  color: '#0f172a'
};

const nameTextCellStyle = {
  ...baseTextCellStyle,
  fontSize: '15px',
  color: '#0f172a'
};

const editableTextCellStyle = {
  ...baseTextCellStyle,
  cursor: 'pointer',
  fontWeight: '600',
  padding: '8px 0',
  display: 'inline-flex',
  alignItems: 'center',
  width: 'fit-content',
  maxWidth: '100%'
};

const inlineEditIconStyle = {
  flex: '0 0 auto',
  color: '#94a3b8',
  marginLeft: '6px',
  transition: 'opacity 140ms ease'
};

const resetSellRateButtonStyle = {
  width: '24px',
  minWidth: '24px',
  height: '24px',
  padding: 0,
  marginLeft: '6px',
  border: '1px solid #bfdbfe',
  borderRadius: '6px',
  color: '#2563eb',
  backgroundColor: '#eff6ff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const qtyStepperStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'flex-start'
};

const qtyStepperButtonStyle = {
  width: '28px',
  height: '28px',
  minWidth: '28px',
  borderRadius: '999px',
  border: 'none',
  backgroundColor: '#f1f5f9',
  color: '#0f172a',
  boxShadow: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const disabledQtyStepperButtonStyle = {
  backgroundColor: '#f8fafc',
  color: '#cbd5e1',
  cursor: 'not-allowed'
};

const qtyValueButtonStyle = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: '#020617',
  fontWeight: '700',
  fontSize: '14px',
  lineHeight: 1
};

const totalTextCellStyle = {
  ...baseTextCellStyle,
  fontWeight: '700',
  padding: '8px 0'
};

const totalQtyTextCellStyle = {
  ...baseTextCellStyle,
  fontWeight: '700',
  padding: '8px 0'
};

const duplicateFieldStyle = {
  backgroundColor: '#fffbeb',
  borderColor: '#f59e0b',
  boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.18)'
};

const duplicateButtonStyle = {
  backgroundColor: '#fffbeb',
  borderColor: '#f59e0b'
};

const totalStyle = {
  height: fieldHeight,
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const disabledInputStyle = {
  backgroundColor: '#f1f5f9',
  color: '#94a3b8',
  cursor: 'not-allowed'
};

const readonlyCellStyle = {
  height: fieldHeight,
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  fontSize: '14px',
  color: '#334155',
  boxSizing: 'border-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const rateViewCellStyle = {
  height: fieldHeight,
  padding: '6px 6px 6px 10px',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  fontSize: '14px',
  color: '#334155',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px'
};

const rateEditCellStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 32px 32px',
  gap: '6px',
  alignItems: 'center'
};

const rateInputStyle = {
  ...inputStyle
};

const iconButtonBaseStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const editButtonStyle = {
  ...iconButtonBaseStyle,
  border: '1px solid #dbeafe',
  backgroundColor: 'white',
  color: '#2563eb'
};

const disabledIconButtonStyle = {
  borderColor: '#e2e8f0',
  backgroundColor: '#f1f5f9',
  color: '#94a3b8',
  cursor: 'not-allowed'
};

const confirmButtonStyle = {
  ...iconButtonBaseStyle,
  border: '1px solid #bbf7d0',
  backgroundColor: '#f0fdf4',
  color: '#16a34a'
};

const cancelButtonStyle = {
  ...iconButtonBaseStyle,
  border: '1px solid #fee2e2',
  backgroundColor: '#fff',
  color: '#ef4444'
};

const emptyGroupStyle = {
  gridColumn: '1 / -1',
  padding: '18px',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  color: '#64748b',
  fontSize: '14px',
  textAlign: 'center'
};

const groupFooterStyle = {
  gridColumn: '1 / -1',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  paddingTop: '14px',
  color: '#1e293b'
};

const groupTotalBlockStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
};

const groupTotalLabelStyle = {
  fontSize: '12px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.08em'
};

const groupTotalAmountStyle = {
  fontSize: '16px',
  fontWeight: '700',
  color: '#020617',
  lineHeight: 1.25
};

const masterActionsRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px'
};

const addButtonStyle = {
  padding: '10px 16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#2563eb',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer'
};

const resetButtonStyle = {
  padding: '10px 16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#dc2626',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer'
};

const removeGroupButtonStyle = {
  borderRadius: '8px',
  fontWeight: '600'
};

const deleteButtonStyle = {
  width: '40px',
  height: '39px',
  borderRadius: '8px',
  border: '1px solid transparent',
  borderColor: 'transparent',
  boxShadow: 'none',
  backgroundColor: 'transparent',
  color: '#ef4444',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 140ms ease, background-color 140ms ease, border-color 140ms ease'
};

const deleteButtonHoverStyle = {
  opacity: 1,
  borderColor: '#fee2e2',
  backgroundColor: '#fff'
};

export default AdminMasterCalc;
